import { CustomerOrderOfferLineView } from "src/api/generated/erp/db/types/tables/customerOrderOfferLineView";
import i18n from "i18next";
import { faCalendarAlt, faClone, faFileImport, faShare, faTools, faVial } from "@fortawesome/pro-regular-svg-icons";
import { AsyncMenuButton } from "src/components/common/contextMenu/AsyncMenuButton.tsx";
import { AavoDataGridRowContextMenuParams } from "src/components/common/dataGrid/types";
import { CustomerOrderOfferLineConfiguratorView } from "src/components/views/erp/configurator/configuratorForm/impl/CustomerOrderOfferLineConfiguratorView.tsx";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { OpenPartConfigurationButton } from "src/components/views/erp/configurator/inspecting/OpenPartConfigurationButton.tsx";
import { CustomerOrderOfferLinesDataGrid } from "src/components/views/erp/sales/customerOrderOffer/CustomerOrderOfferLinesDataGrid.tsx";
import { OpenObjectChangeLogButton } from "src/components/views/changeLogging/OpenObjectChangeLogButton.tsx";
import dayjs from "dayjs";
import { useInputDialog } from "src/components/common/dialogs/input/useInputDialog.tsx";
import { CustomerOrderOfferActionsApi } from "src/api/generated/erp/sales/customerOrderOffer/api/customerOrderOfferActionsApi.ts";
import { dayJsToDateIsoString } from "src/utils/dayjsUtils.ts";
import { OpenConfigurationHistoryButton } from "src/components/views/erp/configurator/objectConfigurationHistory/OpenConfigurationHistoryButton.tsx";
import { Permissions } from "src/api/generated/iam/permissions.ts";
import { useHasPermission } from "src/components/views/erp/common/userPermissions.ts";
import { createCustomerOfferLineConfiguratorType } from "src/components/views/erp/configurator/configuratorForm/utils/partConfiguratorTypeCreators.ts";
import { getConfiguratorFormDialogTitle } from "src/components/views/erp/configurator/configuratorForm/utils/configuratorFormUtils.tsx";
import { ImportPartConfigurationDialog } from "src/components/views/erp/configurator/ImportPartConfigurationDialog.tsx";
import { CustomerOrderOfferLineEditApi } from "src/api/generated/erp/sales/customerOrderOffer/api/customerOrderOfferLineEditApi.ts";
import { canEditCustomerOrderOffer } from "./customerOrderOfferStateGuards.ts";
import { RealObjectTestConfiguratorRootRevisionDialog } from "src/components/views/erp/configurator/configuratorTesting/RealObjectTestConfiguratorRootRevisionDialog.tsx";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface CustomerOrderOfferLinesDataGridRowContextMenuProps extends AavoDataGridRowContextMenuParams<CustomerOrderOfferLineView> {
	refreshData: () => Promise<unknown>;
	editable: boolean;
	configuringHidden: boolean;
}

export const CustomerOrderOfferLinesDataGridRowContextMenu = ({
	onlySingleRowSelected,
	row,
	refreshData,
	allSelectedRows,
	editable,
	configuringHidden,
}: CustomerOrderOfferLinesDataGridRowContextMenuProps) => {
	const { openDialog } = useGenericDialog();
	const showInputDialog = useInputDialog();
	const hasPermission = useHasPermission();
	const canOpenConfigurationDetails = hasPermission(Permissions.Sales.CustomerOffers.READ_COSTS);
	const offerIsClosed = !canEditCustomerOrderOffer(row.offerState);
	const acceptedOfferConfigurationEnabled = row.offerState === "ACCEPTED";
	const offerLineEditingEnabled = editable && !offerIsClosed;
	const configuratorReadonly = !editable || (offerIsClosed && !acceptedOfferConfigurationEnabled);

	return [
		onlySingleRowSelected && row.salesPartType === "PACKAGE_" && (
			<AsyncMenuButton
				key={"openPackageLinesButton"}
				label={i18n.t("package_lines")}
				icon={faShare}
				onClick={() => {
					openDialog(() => ({
						title: i18n.t("package_lines"),
						size: "xl",
						agentContext: {
							key: defineAgentUiContextKey(
								"erp.sales.customerOfferPackageLines.customerOrderOfferLinesDataGridRowContextMenu.dialog",
							),
							pathSegments: [`${i18n.t("offer_line")} ${row.customerOrderOfferLineId}`],
							data: {
								customerOrderOfferId: row.customerOrderOfferId,
								parentCustomerOrderOfferLineId: row.customerOrderOfferLineId,
							},
						},
						content: (
							<CustomerOrderOfferLinesDataGrid
								customerOrderOfferId={row.customerOrderOfferId}
								parentLineId={row.customerOrderOfferLineId}
								editable={editable}
							/>
						),
					}));
				}}
			/>
		),
		!configuringHidden && onlySingleRowSelected && row.partIsConfigurable && (
			<AsyncMenuButton
				key={"configureButton"}
				label={configuratorReadonly ? i18n.t("configure_read_only") : i18n.t("configure")}
				icon={faTools}
				onClick={async () => {
					const configuratorType = createCustomerOfferLineConfiguratorType(row.customerOrderOfferLineId);
					const dialogTitle = await getConfiguratorFormDialogTitle({ configuratorType: configuratorType });
					openDialog(({ closeDialog, onDataDirtyStateChanged }) => ({
						title: dialogTitle,
						agentContext: {
							key: defineAgentUiContextKey("erp.sales.customerOfferLineConfigurator.dialog"),
							pathSegments: [`${i18n.t("offer_line")} ${row.customerOrderOfferLineId}`],
							data: {
								customerOrderOfferId: row.customerOrderOfferId,
								customerOrderOfferLineId: row.customerOrderOfferLineId,
								partConfigurationId: row.partConfigurationId ?? null,
							},
						},
						content: (
							<CustomerOrderOfferLineConfiguratorView
								configuratorType={configuratorType}
								customerOfferId={row.customerOrderOfferId}
								customerOfferState={row.offerState}
								closeDialog={closeDialog}
								refreshSourceView={refreshData}
								disabled={configuratorReadonly}
								onDataDirtyStateChanged={onDataDirtyStateChanged}
							/>
						),
					}));
				}}
			/>
		),
		!configuratorReadonly &&
			onlySingleRowSelected &&
			row.partIsConfigurable &&
			hasPermission(Permissions.Parts.Configurators.TEST) && (
				<AsyncMenuButton
					key={"testConfigureButton"}
					label={i18n.t("test_configuration")}
					icon={faVial}
					onClick={openTestConfigurator}
				/>
			),
		onlySingleRowSelected && canOpenConfigurationDetails && (
			<OpenPartConfigurationButton
				key={"openConfigurationButton"}
				partConfigurationId={row.partConfigurationId}
				vatPercent={row.vatPercent}
				partConfiguratorType={createCustomerOfferLineConfiguratorType(row.customerOrderOfferLineId)}
			/>
		),
		onlySingleRowSelected && row.partConfigurationId != null && (
			<OpenConfigurationHistoryButton
				key={"openConfigurationHistoryButton"}
				objectType={"CUSTOMER_ORDER_OFFER_LINE"}
				objectId={row.customerOrderOfferLineId}
				refreshSourceView={refreshData}
			/>
		),
		!configuringHidden &&
			offerLineEditingEnabled &&
			onlySingleRowSelected &&
			row.partIsConfigurable &&
			row.offerState === "INITIAL" && (
				<AsyncMenuButton
					key={"importConfiguration"}
					label={i18n.t("import_configuration")}
					icon={faFileImport}
					onClick={() => {
						openDialog(({ closeDialog }) => ({
							title: i18n.t("import_configuration"),
							size: "sm",
							agentContext: {
								key: defineAgentUiContextKey("erp.sales.customerOfferLineConfigurationImport.dialog"),
								pathSegments: [i18n.t("import_configuration")],
								data: {
									customerOrderOfferId: row.customerOrderOfferId,
									customerOrderOfferLineId: row.customerOrderOfferLineId,
									partConfigurationId: row.partConfigurationId ?? null,
								},
							},
							content: (
								<ImportPartConfigurationDialog
									onSubmit={async ({ partConfigurationId }) => {
										await CustomerOrderOfferLineEditApi.update({
											request: {
												customerOrderOfferLineId: row.customerOrderOfferLineId,
												partConfigurationId: partConfigurationId,
											},
										});
										await refreshData();
									}}
									closeDialog={closeDialog}
								/>
							),
						}));
					}}
				/>
			),
		offerLineEditingEnabled && onlySingleRowSelected && (
			<AsyncMenuButton
				key={"clone"}
				label={i18n.t("copy")}
				icon={faClone}
				onClick={async () => {
					await CustomerOrderOfferLineEditApi.copyLine({
						customerOrderOfferLineId: row.customerOrderOfferLineId,
					});
					await refreshData();
				}}
			/>
		),
		offerLineEditingEnabled && row.parentLineId == null && (
			<AsyncMenuButton
				key={"changePlannedDeliveryDate"}
				label={i18n.t("change_planned_delivery_date")}
				icon={faCalendarAlt}
				onClick={async () => {
					await showChangePlannedDeliveryDateDialog();
					await refreshData();
				}}
			/>
		),
		onlySingleRowSelected && (
			<OpenObjectChangeLogButton
				key={"changeLog"}
				objectRef={{
					objectType: "CUSTOMER_ORDER_OFFER_LINE",
					objectId: row.customerOrderOfferLineId,
				}}
			/>
		),
	];

	async function openTestConfigurator() {
		const configuratorType = createCustomerOfferLineConfiguratorType(row.customerOrderOfferLineId, true);
		openDialog(({ closeDialog }) => ({
			title: i18n.t("root_revision_to_use"),
			size: "sm",
			agentContext: {
				key: defineAgentUiContextKey("erp.sales.customerOfferLineTestConfiguratorRootRevision.dialog"),
				pathSegments: [i18n.t("test_configuration")],
				data: {
					customerOrderOfferId: row.customerOrderOfferId,
					customerOrderOfferLineId: row.customerOrderOfferLineId,
					partConfigurationId: row.partConfigurationId ?? null,
				},
			},
			content: (
				<RealObjectTestConfiguratorRootRevisionDialog
					configuratorType={configuratorType}
					closeDialog={closeDialog}
					onSubmit={async () => {
						const dialogTitle = await getConfiguratorFormDialogTitle({
							configuratorType: configuratorType,
						});
						openDialog(({ closeDialog, onDataDirtyStateChanged }) => ({
							title: dialogTitle,
							agentContext: {
								key: defineAgentUiContextKey("erp.sales.customerOfferLineTestConfigurator.dialog"),
								pathSegments: [`${i18n.t("offer_line")} ${row.customerOrderOfferLineId}`],
								data: {
									customerOrderOfferId: row.customerOrderOfferId,
									customerOrderOfferLineId: row.customerOrderOfferLineId,
									partConfigurationId: row.partConfigurationId ?? null,
								},
							},
							content: (
								<CustomerOrderOfferLineConfiguratorView
									configuratorType={configuratorType}
									customerOfferId={row.customerOrderOfferId}
									customerOfferState={row.offerState}
									closeDialog={closeDialog}
									refreshSourceView={refreshData}
									disabled={configuratorReadonly}
									onDataDirtyStateChanged={onDataDirtyStateChanged}
								/>
							),
						}));
					}}
				/>
			),
		}));
	}

	async function showChangePlannedDeliveryDateDialog() {
		const newPlannedDeliveryDate = await showInputDialog({
			defaultValue: dayjs(row.plannedDeliveryDate),
			type: "date",
			title: i18n.t("change_planned_delivery_date"),
			fieldLabel: i18n.t("new_delivery_date"),
			required: true,
		});
		if (newPlannedDeliveryDate == null) return;

		await CustomerOrderOfferActionsApi.updateOfferLinePlannedDeliveryDates({
			customerOrderOfferLineIds: allSelectedRows.map((r) => r.customerOrderOfferLineId),
			newPlannedDeliveryDate: dayJsToDateIsoString(newPlannedDeliveryDate),
		});
	}
};
